import { Injectable, inject } from '@angular/core';
import { EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ImportedInvoiceMappingStatus, IntegrationProviderEnum, JobFilterInput, JobItemFilterInput, Uuid } from '../../../../graphql';
import { IntegrationProvider } from '../../../data/models/integrations.model';
import { AccountingImportStoreService, Category, JobInfo } from './domain/accounting-import.store.service';
import { AccountingImportApiService } from './domain/api/accounting-import.api.service';

@Injectable({ providedIn: 'root' })
export class AccountingImportService {
    #api = inject(AccountingImportApiService);
    #store = inject(AccountingImportStoreService);

    getFilteredJobs(searchTerm: string) {
        const where: JobFilterInput = { and: [{ isDeleted: { neq: true } }] };
        if (searchTerm) where.and?.push({ or: [{ number: { contains: searchTerm } }, { description: { contains: searchTerm } }] });
        return this.#api
            .getJobDropdown({
                where,
                order: { createdAt: 'DESC' },
                take: 20,
            })
            .pipe(
                tap(({ data }) => {
                    if (!searchTerm && data.jobs) this.#store.initialJobItems.set(data.jobs);
                })
            );
    }

    getFilteredJobCategories(jobId: Uuid, searchTerm: string) {
        const where: JobItemFilterInput = { and: [{ isDeleted: { neq: true } }, { parentId: { eq: null } }] };
        if (searchTerm)
            where.and?.push({ or: [{ description: { contains: searchTerm } }, { displayedOrderNumber: { contains: searchTerm } }] });

        return this.#api.getJobItemsDropdown({
            jobId,
            where,
            order: { orderNumber: 'ASC' },
            take: 20,
        });
    }

    getFilteredItems(jobId: Uuid, parentId: Uuid, searchTerm: string) {
        const where: JobItemFilterInput = {
            and: [{ isDeleted: { neq: true } }, { parentId: { eq: parentId } }],
        };
        if (searchTerm)
            where.and?.push({ or: [{ description: { contains: searchTerm } }, { displayedOrderNumber: { contains: searchTerm } }] });

        return this.#api.getJobItemsDropdown({
            jobId,
            where,
            order: { orderNumber: 'ASC' },
            take: 20,
        });
    }

    getCategoryWithDescription(jobId: Uuid, description: string) {
        return this.#api.getCategoryWithDescription({ jobId, description });
    }

    getCostingItemWithDescription(jobId: Uuid, parentId: Uuid, description: string) {
        return this.#api.getCostingItemWithDescription({ jobId, parentId, description });
    }

    getBillInvoices(intProvider: IntegrationProvider, contactName: string, invoiceNumber: string) {
        const { page, hasNextPage: shouldFetch, items: existingInvoices } = this.#store.accountingInvoices.value ?? { page: 0, items: [] };
        if (shouldFetch === false) return EMPTY;
        const provider = this.#providerToProviderEnum(intProvider);
        return this.#api.getBillInvoices({ provider, page, contactName, invoiceNumber }).pipe(
            tap(({ data }) => {
                const { invoices, hasNextPage, nextPageNumber } = data?.accountingBillInvoices ?? { nextPageNumber: page };
                if (!invoices) return;
                invoices.forEach(({ id, jobForInvoice, lineItems }) => {
                    if (!jobForInvoice) return;
                    const lineItemIds = lineItems.map((li) => li.id);
                    this.setInvoiceJob(id, lineItemIds, jobForInvoice);
                    lineItems.forEach((li) => this.setLineItemCategory(id, li.id, li.jobItem ?? li.category ?? undefined));
                });
                const items = [...existingInvoices, ...invoices.filter(({ id }) => !this.#store.accountingInvoices.invoiceExists(id))];
                this.#store.accountingInvoices.set({ items, page: nextPageNumber, hasNextPage });
            })
        );
    }

    setInvoiceJob(id: string, lineItemIds: string[], jobForInvoice?: JobInfo) {
        if (!jobForInvoice) {
            this.#store.invoiceMap.delete(id);
            lineItemIds.forEach((lineItemId) => this.#store.lineItemMap.delete(lineItemId));
        } else {
            this.#store.invoiceMap.set(id, { job: jobForInvoice });
            lineItemIds.forEach((lineItemId) => this.#store.lineItemMap.set(lineItemId, { job: jobForInvoice, invoiceId: id }));
        }
    }

    setLineItemJob(invoiceId: string, id: string, job?: JobInfo) {
        job ? this.#store.lineItemMap.set(id, { job, invoiceId }) : this.#store.lineItemMap.delete(id);
    }

    setInvoiceCategory(invoiceId: string, lineItemIds: string[], category?: Category | string) {
        const job = this.#store.invoiceMap.value.get(invoiceId)?.job;
        if (!job) return;
        this.#store.invoiceMap.set(invoiceId, { job, category });

        lineItemIds.forEach((lineItemId) => {
            const lineItemJob = this.#store.lineItemMap.value.get(lineItemId)?.job;
            if (lineItemJob?.iD_Job !== job.iD_Job) return;
            this.#store.lineItemMap.set(lineItemId, { job, category, invoiceId });
        });
    }

    setLineItemCategory(invoiceId: string, lineItemId: string, category?: Category | string) {
        const job = this.#store.lineItemMap.value.get(lineItemId)?.job;
        this.#store.lineItemMap.set(lineItemId, { job, category, invoiceId });
    }

    setInvoiceCostingItem(invoiceId: string, lineItemIds: string[], item?: Category | string) {
        const { job, category } = this.#store.invoiceMap.value.get(invoiceId) ?? {};
        if (!job || !category) return;
        this.#store.invoiceMap.set(invoiceId, { job, category, item });
        lineItemIds.forEach((lineItemId) => {
            const { job: lineItemJob, category: lineItemCategory } = this.#store.lineItemMap.value.get(lineItemId) ?? {};
            const matchesInvoiceCategory =
                lineItemCategory === category ||
                (typeof lineItemCategory === 'object' &&
                    typeof category === 'object' &&
                    lineItemCategory.iD_JobItem === category.iD_JobItem);
            if (lineItemJob?.iD_Job !== job.iD_Job || !matchesInvoiceCategory) return;
            this.#store.lineItemMap.set(lineItemId, { job, category: lineItemCategory, item, invoiceId });
        });
    }

    setLineItemCostingItem(invoiceId: string, lineItemId: string, item?: Category | string) {
        const { job, category } = this.#store.lineItemMap.value.get(lineItemId) ?? {};
        this.#store.lineItemMap.set(lineItemId, { job, category, item, invoiceId });
    }

    addIgnoredInvoice(intProvider: IntegrationProvider, invoiceId: string, lineItemId?: string) {
        const provider = this.#providerToProviderEnum(intProvider);
        return this.#api.addImportedInvoiceMapping(provider, invoiceId, ImportedInvoiceMappingStatus.Ignored, lineItemId);
    }

    addMappedInvoice(intProvider: IntegrationProvider, invoiceId: string, bxPurchaseOrWorkOrderId: Uuid, lineItemId?: string) {
        const provider = this.#providerToProviderEnum(intProvider);
        return this.#api.addImportedInvoiceMapping(
            provider,
            invoiceId,
            ImportedInvoiceMappingStatus.Mapped,
            lineItemId,
            bxPurchaseOrWorkOrderId
        );
    }

    removeInvoiceFromStore(invoiceId: string) {
        const invoice = this.#store.accountingInvoices.value?.items?.find((v) => v.id === invoiceId);
        if (invoice) {
            this.#store.accountingInvoices.delete(invoiceId);
            this.#store.invoiceMap.delete(invoiceId);
            invoice.lineItems.forEach((li) => this.#store.lineItemMap.delete(li.id));
        }
    }

    removeLineItemFromStore(invoiceId: string, lineItemId: string) {
        const invoice = this.#store.accountingInvoices.value?.items?.find((v) => v.id === invoiceId);
        if (invoice) {
            invoice.lineItems = invoice.lineItems.filter((li) => li.id !== lineItemId);
            this.#store.accountingInvoices.set(this.#store.accountingInvoices.value);
            this.#store.lineItemMap.delete(lineItemId);

            if (!invoice.lineItems.length) {
                this.removeInvoiceFromStore(invoiceId);
            }
        }
    }
    getInvoiceAttachments(invoiceId: string) {
        const intPro = this.#store.provider.value;
        if (!intPro) return;
        const provider = this.#providerToProviderEnum(intPro);
        return this.#api.getInvoiceAttachments({
            invoiceId,
            provider,
            order: { contentLength: 'ASC' },
            take: 1000,
        });
    }
    copyAttachment(invoiceId: string, attachmentId: Uuid) {
        const intPro = this.#store.provider.value;
        if (!intPro) return;
        const provider = this.#providerToProviderEnum(intPro);
        return this.#api.copyAttachment({ attachmentId, invoiceId, provider });
    }

    copyAttachmentsFromExternalInvoices(invoiceId: string, intPro: IntegrationProvider, purchaseOrderId: Uuid) {
        const provider = this.#providerToProviderEnum(intPro);
        return this.#api.copyAttachmentsFromExternalInvoices(invoiceId, provider, purchaseOrderId);
    }

    #providerToProviderEnum(provider: IntegrationProvider) {
        switch (provider) {
            case IntegrationProvider.Xero:
                return IntegrationProviderEnum.Xero;
            case IntegrationProvider.QuickBooks:
                return IntegrationProviderEnum.Quickbooks;
            case IntegrationProvider.MYOB:
                return IntegrationProviderEnum.Myob;
            case IntegrationProvider.Deputy:
                return IntegrationProviderEnum.Deputy;
            case IntegrationProvider.QuickbooksTimesheets:
                return IntegrationProviderEnum.Quickbookstimesheets;
        }
    }
}
